import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  CheckboxSelectionCallbackParams,
  ColDef,
  RowSelectedEvent,
  ValueGetterParams,
} from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import AgGrid from "components/UI/AgGrid";
import CustomizedButton from "components/Custom/CustomizedButton";
import ModalUI from "components/UI/Modal/ModalUI";
import ButtonLayout from "components/UI/ButtonLayout";
import { useTranslation } from "react-i18next";
import HeaderSelectAll from "./HeaderSelectAll";

type ExtendedProps = {
  gridRef: any;
  modalTitle: string;
  cancelBtnTitle?: string;
  btnTitle: string;
  modalIsOpen: boolean;
  height: number | string;
  onFinishEditing: (isSelectAll: boolean, selectedData: any, excludedIds?: string[]) => void;
  closeModal: () => void;
  selectedIds: string[];
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
  idsSnapshot: string[];
  setIdsSnapshot: React.Dispatch<React.SetStateAction<string[]>>;
  lockRows?: (string | undefined)[];
  disabledSidebar?: boolean;
  rowsToDisable?: any;
  columnDefs: ColDef[];
  isLoading?: boolean;
  keyName?: string;
};

type CheckboxAggridModalSSProps = AgGridReactProps & ExtendedProps;

const CheckboxAggridModalSS: React.FC<CheckboxAggridModalSSProps> = ({
  gridRef,
  modalTitle,
  modalIsOpen,
  columnDefs,
  rowSelection,
  height,
  rowData,
  onFinishEditing,
  enableRangeSelection,
  closeModal,
  selectedIds,
  setSelectedIds,
  idsSnapshot,
  setIdsSnapshot,
  lockRows,
  getRowId,
  rowsToDisable,
  onFirstDataRendered,
  masterDetail,
  detailCellRendererParams,
  onRowClicked,
  onRowDoubleClicked,
  disabledSidebar,
  isLoading,
  onGridReady,
  children,
  keyName,
  onRowDataUpdated,
}: CheckboxAggridModalSSProps) => {
  const { t } = useTranslation();
  const [updatedColumnDefs, setUpdatedColumnDefs] = useState<ColDef<any>[]>([]);

  // isSelectAll: true  → confirm fetches everything from server (minus excludedIds)
  //              false → confirm filters by selectedIds
  const [isSelectAll, setIsSelectAll] = useState(false);
  const isSelectAllRef = useRef(false);

  // Tracks rows deselected while in select-all mode.
  // Staying in select-all mode avoids losing the "all selected" intent.
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const excludedIdsRef = useRef<string[]>([]);

  const isIndeterminate =
    (!isSelectAll && selectedIds.length > 0) ||
    (isSelectAll && excludedIds.length > 0);

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!modalIsOpen) {
      setIsSelectAll(false);
      isSelectAllRef.current = false;
      setExcludedIds([]);
      excludedIdsRef.current = [];
    }
  }, [modalIsOpen]);

  // ─── Grid ready ──────────────────────────────────────────────────────────────
  const handleGridReady = useCallback(
    (params: any) => {
      if (isSelectAllRef.current) {
        params.api.setServerSideSelectionState({
          selectAll: true,
          toggledNodes: [],
        });
      }
      onGridReady?.(params);
    },
    [onGridReady],
  );

  // ─── Header toggle ───────────────────────────────────────────────────────────
  const handleToggleAll = useCallback(
    (checked: boolean) => {
      isSelectAllRef.current = checked;
      setIsSelectAll(checked);
      setSelectedIds([]);
      setExcludedIds([]);
      excludedIdsRef.current = [];
      if (gridRef.current?.api) {
        gridRef.current.api.setServerSideSelectionState({
          selectAll: checked,
          toggledNodes: [],
        });
      }
    },
    [gridRef, setSelectedIds],
  );

  // ─── Column defs ─────────────────────────────────────────────────────────────
  const checkboxColumn = useMemo(() => {
    return {
      ...columnDefs[0],
      headerCheckboxSelection: false,
      headerComponent: HeaderSelectAll,
      headerComponentParams: {
        isAllSelected: isSelectAll,
        isIndeterminate,
        onToggleAll: handleToggleAll,
      },
      checkboxSelection: (params: CheckboxSelectionCallbackParams) => {
        if (!rowsToDisable || rowsToDisable.length === 0) return true;
        const idKeyName = keyName ? keyName : columnDefs[0]?.field;
        return !idKeyName || !rowsToDisable.includes(params?.data[idKeyName]);
      },
      // Pure valueGetter — no setSelected side-effects.
      // Pre-selection is handled by the datasource after each block loads
      // (via setTimeout) to avoid triggering SSRM cache invalidation.
      valueGetter: (params: ValueGetterParams) => {
        const idKeyName = columnDefs[0]?.field;
        return idKeyName ? params?.data[idKeyName] : undefined;
      },
    };
  }, [
    columnDefs,
    rowsToDisable,
    keyName,
    isSelectAll,
    isIndeterminate,
    handleToggleAll,
  ]);

  useEffect(() => {
    if (columnDefs && checkboxColumn) {
      const newColumnDefs: ColDef[] = [checkboxColumn, ...columnDefs.slice(1)];
      setUpdatedColumnDefs(newColumnDefs);
    }
  }, [columnDefs, rowsToDisable, checkboxColumn]);

  // ─── Row selection ───────────────────────────────────────────────────────────
  const handleRowSelection = (params: RowSelectedEvent) => {
    // Skip events fired by API calls (setServerSideSelectionState, node.setSelected).
    // Only process genuine user interactions.
    if ((params as any).source === "api") return;

    const idKeyName = keyName ? keyName : columnDefs[0]?.field;
    if (!idKeyName) return setSelectedIds([]);

    const selectedId = params?.node?.data[idKeyName];
    if (!selectedId) {
      setSelectedIds([]);
      return;
    }

    if (rowsToDisable?.includes(selectedId)) {
      params?.node?.setSelected(false);
    } else if (lockRows?.includes(selectedId)) {
      params?.node?.setSelected(true);
    } else if (isSelectAllRef.current) {
      // In select-all mode: track exclusions rather than exiting the mode.
      // This preserves the "all selected" intent for rows that aren't loaded yet.
      if (params?.node?.isSelected()) {
        // Re-selecting a previously excluded row → remove from exclusions
        setExcludedIds((prev) => prev.filter((id) => id !== selectedId));
        excludedIdsRef.current = excludedIdsRef.current.filter(
          (id) => id !== selectedId,
        );
      } else {
        // Deselecting a row → add to exclusions
        setExcludedIds((prev) =>
          prev.includes(selectedId) ? prev : [...prev, selectedId],
        );
        excludedIdsRef.current = [...excludedIdsRef.current, selectedId];
      }
    } else {
      if (params?.node?.isSelected()) {
        setSelectedIds((prevIds: string[]) =>
          prevIds.includes(selectedId) ? prevIds : [...prevIds, selectedId],
        );
      } else {
        setSelectedIds((prevIds: string[]) =>
          prevIds.filter((id: string) => id !== selectedId),
        );
      }
    }
  };

  const handleFinishSelecting = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    const selectedData =
      rowSelection === "single" ? selectedRows[0] : selectedRows;

    setIdsSnapshot(selectedIds);
    onFinishEditing(isSelectAll, selectedData, excludedIds);

    setIsSelectAll(false);
    isSelectAllRef.current = false;
    setExcludedIds([]);
    excludedIdsRef.current = [];

    closeModal();
  };

  const handleCancelModal = () => {
    setSelectedIds(idsSnapshot);
    setIsSelectAll(false);
    isSelectAllRef.current = false;
    setExcludedIds([]);
    excludedIdsRef.current = [];
    closeModal();
  };

  return (
    <ModalUI
      open={modalIsOpen}
      handleClose={handleCancelModal}
      title={modalTitle}
      fullWidth
      maxWidth="lg"
      isLoading={isLoading}
      action={
        <ButtonLayout>
          <CustomizedButton
            title={t("button.cancel")}
            variant="outlined"
            size="medium"
            onClick={handleCancelModal}
          />
          <CustomizedButton
            title={t("button.confirm")}
            onClick={handleFinishSelecting}
            variant="contained"
            size="medium"
          />
        </ButtonLayout>
      }
    >
      {children}
      <AgGrid
        ref={gridRef}
        columnDefs={updatedColumnDefs}
        height={height}
        onGridReady={handleGridReady}
        rowSelection={rowSelection}
        rowData={rowData}
        onRowSelected={handleRowSelection}
        onRowClicked={onRowClicked}
        onRowDoubleClicked={onRowDoubleClicked}
        enableRangeSelection={enableRangeSelection}
        getRowId={getRowId}
        rowMultiSelectWithClick={rowSelection !== "single"}
        onFirstDataRendered={onFirstDataRendered}
        masterDetail={masterDetail}
        detailCellRendererParams={detailCellRendererParams}
        disabledSidebar={disabledSidebar}
        onRowDataUpdated={onRowDataUpdated}
      />
    </ModalUI>
  );
};

export default CheckboxAggridModalSS;
